import { motion } from "framer-motion";
import {
  FaSolarPanel,
  FaTools,
  FaShieldAlt,
  FaCog,
  FaHeadset,
  FaWrench
} from "react-icons/fa";
import bgVideo from "../assets/mixkit-installing-a-solar-panel-close-up-23276-hd-ready.mp4";
import moduleVideo from "../assets/video/modulemaunting.mp4";
import "../styles/Structure.scss";

const features = [
  {
    icon: FaSolarPanel,
    text: "Precision-engineered module mounting structures for maximum strength and stability"
  },
  {
    icon: FaShieldAlt,
    text: "High-quality, corrosion-resistant materials for long service life"
  },
  {
    icon: FaCog,
    text: "Customized design and manufacturing as per project requirements"
  },
  {
    icon: FaTools,
    text: "Compliance with industry standards and quality guidelines"
  },
  {
    icon: FaHeadset,
    text: "Reliable after-sales support and technical assistance"
  },
  {
    icon: FaWrench,
    text: "Easy installation and low maintenance solutions"
  }
];

const stats = [
  { number: "500+", label: "Projects Completed" },
  { number: "50MW+", label: "Total Capacity" },
  { number: "15+", label: "Years Experience" },
  { number: "99%", label: "Client Satisfaction" }
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const Structure = () => {
  return (
    <section className="structure-section py-5">
      <div className="container-fluid px-3 px-md-4">
        {/* SEO Hidden Heading */}
        <h1 className="visually-hidden">
          MODULE MOUNTING STRUCTURE MANUFACTURERS
        </h1>

        {/* Hero Section */}
        <motion.div
          className="hero-container"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <video
            className="hero-video"
            src={bgVideo}
            autoPlay
            loop
            muted
            playsInline
            aria-hidden="true"
          />
          <div className="hero-overlay">
            <div className="hero-content px-3">
              <motion.h2
                className="hero-title"
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                MODULE MOUNTING STRUCTURE MANUFACTURERS
              </motion.h2>
              <motion.p
                className="hero-subtitle"
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Professionally engineered mounting structures providing strong,
                secure and long-lasting support for solar module installations in
                all operating conditions.
              </motion.p>
            </div>
          </div>
        </motion.div>
        <div className="container">
          {/* Main Content */}
          <div className="main-content">
            <div className="row g-4">
              {/* Features Section */}
              <div className="col-12 text-center">
                <motion.h3
                  className="section-title"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  Key Features & Services
                </motion.h3>
              </div>
              <div className="col-lg-6">
                <motion.div
                  className="features-grid"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <div className="row g-3">
                    {features.map((feature, index) => (
                      <div key={index} className="col-md-6">
                        <motion.div
                          className="feature-card h-100"
                          variants={itemVariants}
                          whileHover={{
                            scale: 1.02,
                            transition: { duration: 0.2 }
                          }}
                        >
                          <feature.icon className="feature-icon" />
                          <p className="feature-text">{feature.text}</p>
                        </motion.div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>

              {/* Video Section */}
              <div className="col-lg-6">
                <motion.div
                  className="video-section"
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <div className="video-container">
                    <video
                      src={moduleVideo}
                      className="module-video"
                      autoPlay
                      loop
                      muted
                      playsInline
                      controls={false}
                      preload="metadata"
                    />
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          {/* Stats Section */}
          <motion.div
            className="stats-section"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="row g-4">
              {stats.map((stat, index) => (
                <div key={index} className="col-6 col-md-3">
                  <motion.div
                    className="stat-item"
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.6,
                      delay: index * 0.1,
                      ease: "easeOut"
                    }}
                    whileHover={{ scale: 1.05 }}
                  >
                    <span className="stat-number">{stat.number}</span>
                    <span className="stat-label">{stat.label}</span>
                  </motion.div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Structure;

